import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { getAvatarUrl } from '@/utils/storage';
import { useToast } from '@/hooks/use-toast';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, Search, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';

interface DebugResult {
  step: string;
  status: 'success' | 'error' | 'info';
  message: string;
  data?: any;
}

export default function AvatarDebugTool() {
  const [userId, setUserId] = useState('');
  const [bucketName, setBucketName] = useState<'client.photo' | 'practitioner.photo'>('client.photo');
  const [isDebugging, setIsDebugging] = useState(false);
  const [debugResults, setDebugResults] = useState<DebugResult[]>([]);
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const { toast } = useToast();

  const addDebugResult = (step: string, status: 'success' | 'error' | 'info', message: string, data?: any) => {
    const result: DebugResult = { step, status, message, data };
    setDebugResults(prev => [...prev, result]);
    console.log(`[AVATAR DEBUG] ${step}: ${message}`, data);
  };

  const debugUserAvatar = async () => {
    if (!userId) {
      toast({
        title: "参数缺失",
        description: "请输入用户ID",
        variant: "destructive"
      });
      return;
    }

    setIsDebugging(true);
    setDebugResults([]);
    setAvatarUrl('');

    try {
      addDebugResult('开始调试', 'info', `调试用户 ${userId} 的头像，存储桶: ${bucketName}`);

      // Step 1: Check if user profile exists
      const tableName = bucketName === 'client.photo' ? 'clients' : 'practitioners';
      const { data: userProfile, error: profileError } = await supabase
        .from(tableName)
        .select('id, first_name, last_name, avatar_url')
        .eq('id', userId)
        .single();

      if (profileError || !userProfile) {
        addDebugResult('用户查找', 'error', `用户不存在或无权访问: ${profileError?.message}`);
        return;
      }

      addDebugResult('用户查找', 'success', `找到用户: ${userProfile.first_name} ${userProfile.last_name}`, {
        avatar_url: userProfile.avatar_url
      });

      // Step 2: List files in user's storage folder
      const { data: files, error: listError } = await supabase.storage
        .from(bucketName)
        .list(userId);

      if (listError) {
        addDebugResult('存储文件列表', 'error', `无法列出文件: ${listError.message}`);
      } else {
        addDebugResult('存储文件列表', 'success', `找到 ${files?.length || 0} 个文件`, files);
      }

      // Step 3: Test avatar URL generation
      if (userProfile.avatar_url) {
        const generatedUrl = getAvatarUrl(bucketName, userProfile.avatar_url);
        setAvatarUrl(generatedUrl);
        addDebugResult('URL生成', 'success', `生成的头像URL: ${generatedUrl}`);

        // Step 4: Test URL accessibility
        try {
          const response = await fetch(generatedUrl, { method: 'HEAD' });
          if (response.ok) {
            addDebugResult('URL可访问性', 'success', `头像文件可访问 (状态: ${response.status})`);
          } else {
            addDebugResult('URL可访问性', 'error', `头像文件不可访问 (状态: ${response.status})`);
          }
        } catch (fetchError) {
          addDebugResult('URL可访问性', 'error', `网络请求失败: ${fetchError}`);
        }
      } else {
        addDebugResult('头像路径', 'info', '用户未设置头像');
      }

      // Step 5: Check storage bucket configuration
      const { data: buckets, error: bucketError } = await supabase.storage.listBuckets();
      if (bucketError) {
        addDebugResult('存储桶配置', 'error', `无法获取存储桶信息: ${bucketError.message}`);
      } else {
        const targetBucket = buckets.find(b => b.name === bucketName);
        if (targetBucket) {
          addDebugResult('存储桶配置', 'success', `存储桶 ${bucketName} 配置正常`, {
            public: targetBucket.public,
            created_at: targetBucket.created_at
          });
        } else {
          addDebugResult('存储桶配置', 'error', `存储桶 ${bucketName} 不存在`);
        }
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      addDebugResult('调试过程', 'error', errorMessage);
      toast({
        title: "调试失败",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsDebugging(false);
    }
  };

  const clearResults = () => {
    setDebugResults([]);
    setAvatarUrl('');
  };

  const getStatusIcon = (status: 'success' | 'error' | 'info') => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case 'info':
        return <Search className="h-4 w-4 text-blue-600" />;
    }
  };

  const getStatusBadge = (status: 'success' | 'error' | 'info') => {
    const variants = {
      success: 'default',
      error: 'destructive',
      info: 'secondary'
    } as const;
    
    return <Badge variant={variants[status]}>{status}</Badge>;
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          头像调试工具
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Debug Configuration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="user-id">用户ID</Label>
            <Input
              id="user-id"
              placeholder="输入用户UUID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bucket-name">存储桶</Label>
            <select 
              className="w-full p-2 border rounded-md"
              value={bucketName}
              onChange={(e) => setBucketName(e.target.value as 'client.photo' | 'practitioner.photo')}
            >
              <option value="client.photo">client.photo</option>
              <option value="practitioner.photo">practitioner.photo</option>
            </select>
          </div>
        </div>

        {/* Debug Actions */}
        <div className="flex gap-2">
          <Button 
            onClick={debugUserAvatar} 
            disabled={isDebugging || !userId}
            className="flex items-center gap-2"
          >
            <Search className="h-4 w-4" />
            {isDebugging ? '调试中...' : '开始调试头像'}
          </Button>
          
          {debugResults.length > 0 && (
            <Button 
              variant="outline" 
              onClick={clearResults}
            >
              清除结果
            </Button>
          )}
        </div>

        {/* Avatar Preview */}
        {avatarUrl && (
          <div className="space-y-2">
            <Label>头像预览</Label>
            <div className="flex items-center gap-4">
              <Avatar className="h-20 w-20">
                <AvatarImage 
                  src={avatarUrl} 
                  alt="调试头像"
                  onError={(e) => {
                    console.error("Debug avatar failed to load:", avatarUrl);
                  }}
                />
                <AvatarFallback>
                  <User size={24} />
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="text-sm font-medium">当前头像URL:</p>
                <Textarea 
                  value={avatarUrl} 
                  readOnly 
                  className="text-xs"
                  rows={2}
                />
              </div>
            </div>
          </div>
        )}

        {/* Debug Results */}
        {debugResults.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">调试结果</h3>
              <Badge variant="outline">{debugResults.length} 条记录</Badge>
            </div>
            
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {debugResults.map((result, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 p-3 border rounded-lg bg-muted/30"
                >
                  {getStatusIcon(result.status)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{result.step}</span>
                      {getStatusBadge(result.status)}
                    </div>
                    <p className="text-sm text-muted-foreground break-words">{result.message}</p>
                    {result.data && (
                      <details className="mt-2">
                        <summary className="text-xs text-muted-foreground cursor-pointer">查看详细数据</summary>
                        <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-x-auto">
                          {JSON.stringify(result.data, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">调试步骤：</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• 输入要调试的用户ID（UUID格式）</li>
            <li>• 选择对应的存储桶（客户端或疗愈师）</li>
            <li>• 点击"开始调试头像"按钮</li>
            <li>• 查看调试结果了解头像加载问题</li>
            <li>• 检查存储桶配置和文件权限</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}